import React, { useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { FormValues, getDispositionExerciseOptions } from '../../UpsertGrantModal.meta';
import { useStockOptionsContext } from '../../../../context/useStockOptionsContext';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { InputNumberMask } from '../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { GranTypeDTO } from '../../../../../../Profile/Accounts/api/useAccountsQuery';
import { Input } from '../../../../../../CommonComponents/OPL/Input/Input';
import { EXCLUDE_OPTION_VALUE, excludeOption } from './Summary.meta';
import { InputMask } from '../../../../../../CommonComponents/OPL/Input/InputMask';
import { SingleDatePicker } from '../../../../../../CommonComponents/OPL/dates/SingleDatePicker/SingleDatePicker';
import { Label } from '../../../../../../CommonComponents/OPL/Label/Label';
import { SearchSymbolsSelect } from '../../../../../../Profile/Accounts/Features/Holdings/components/UpsertHoldingModal/components/SearchSymbolsSelect/SearchSymbolsSelect';
import { SymbolOption } from '../../../../../../Profile/Accounts/Features/Holdings/components/UpsertHoldingModal/components/SearchSymbolsSelect/useSearchSymbolsSelect';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';

interface Props {
  isAddingNewGrant?: boolean;
  clientId: string;
}

export const Summary = ({ isAddingNewGrant, clientId }: Props) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    control,
    watch,
    register,
    formState: { errors },
    setValue,
  } = useFormContext<FormValues>();
  const { options } = useStockOptionsContext();

  const [
    grantType,
    managementStyleId,
    totalShares,
    product,
    name,
    assetClass,
    price,
    vestingList,
  ] = watch([
    'grantType',
    'orionManagementStyleId',
    'totalShares',
    'product',
    'name',
    'assetClass',
    'price',
    'vestingList',
  ]);

  const watchedOwnerModel = useWatch({
    name: 'ownerModel',
    control,
  });

  const [selectedSymbol, setSelectedSymbol] = useState<SymbolOption | null>(null);

  // Initialize selectedSymbol with existing product data on mount
  React.useEffect(() => {
    if (product && !selectedSymbol) {
      setSelectedSymbol({
        label: product.ticker || name,
        value: product.ticker || name,
        subLabel: name,
        data: {
          productId: product.id,
          ticker: product.ticker,
          name,
          price: product.price,
          assetClass,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ownerOptions = [
    {
      label: 'People',
      options: options.OwnerOptions.People.map(owner => ({
        value: owner.Id,
        label: owner.Name,
      })),
    },
    {
      label: 'Trusts',
      options: options.OwnerOptions.Trusts.map(owner => ({
        value: owner.Id,
        label: owner.Name,
      })),
    },
  ];

  const managementStyleOptions = options.ManagementStyles.map(option => ({
    value: option.id,
    label: option.name,
    defaultBucket: option.defaultBucket,
  }));

  const managementStyleSelectedOption = managementStyleOptions.find(
    option => option.value === managementStyleId,
  );

  const availableAssetBucketOptions = options.AvailableAssetBuckets.map(({ Title }, index) => ({
    label: Title,
    value: index,
  }));

  const assetBucketOptions = [...availableAssetBucketOptions, excludeOption].map(option => {
    if (options.AssetBucketingByManagementStyle) {
      const isDefault = !managementStyleSelectedOption
        ? option.value === EXCLUDE_OPTION_VALUE
        : managementStyleSelectedOption.defaultBucket === null
        ? option.value === EXCLUDE_OPTION_VALUE
        : option.value === managementStyleSelectedOption.defaultBucket;

      return {
        ...option,
        rightLabel: isDefault ? 'Default' : undefined,
      };
    }

    return option;
  });

  // Auto-calculate market value when price or totalShares changes and a product is selected
  const marketValue =
    typeof price === 'number' && typeof totalShares === 'number' ? price * totalShares : null;

  React.useEffect(() => {
    if (marketValue !== null) {
      setValue('marketValue', marketValue);
    }
  }, [marketValue, setValue]);

  return (
    <Box display="flex" flexDirection="column" pt="12" css="gap: 16px">
      {errors?.vestingList && (
        <Alert
          variant="error"
          message={'Please fill the required fields at Details, Timing & Strategy'}
          mb="16"
        />
      )}
      <SearchSymbolsSelect
        clientId={+clientId}
        label="Symbol/Name"
        placeholder="Search for a listed company..."
        value={selectedSymbol}
        onChange={opt => {
          setSelectedSymbol(opt);

          if (opt) {
            const { data } = opt as SymbolOption;

            // Auto-populate fields with selected symbol data
            setValue('name', data.name);
            setValue('product', {
              id: data.productId as number,
              ticker: data.ticker,
              price: data.price,
            });
            setValue('assetClass', data.assetClass);
            setValue('price', data.price);

            // Calculate market value if totalShares is set
            if (typeof totalShares === 'number') {
              setValue('marketValue', data.price * totalShares);
            }
          } else {
            // Clear product details when symbol is cleared
            setValue('product', null);
            setValue('price', 0);
            setValue('marketValue', 0);
          }
        }}
        isClearable
        isPortal
      />

      <Input
        {...register('name')}
        label="Company Name"
        maxLength={144}
        formVariant={errors.name ? 'error' : undefined}
        helperText={errors.name?.message}
      />

      <Controller
        control={control}
        name="price"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            label="Price Per Share"
            leftIcon="far fa-dollar-sign"
            decimalScale={2}
            allowNegative={false}
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            disabled={!!product}
            {...field}
          />
        )}
      />

      <Controller
        name="owner"
        control={control}
        render={({ field: { value, onChange, ref } }) => (
          <Select
            innerRef={ref}
            label="Owner"
            value={ownerOptions
              .map(({ options: option }) => option)
              .flat()
              .find(option => option.label === value)}
            options={ownerOptions}
            onChange={opt => {
              onChange(opt?.label);
              if (opt) {
                const selectedOwnerOption = Object.values(options.OwnerOptions)
                  .map(items => items)
                  .flat()
                  .find(option => option.Name === opt.label);
                if (selectedOwnerOption) {
                  setValue('ownerModel', [
                    {
                      ...watchedOwnerModel[0],
                      Id: opt.value,
                      Name: opt.label,
                      OwnerType: selectedOwnerOption.OwnerType,
                      Percentage: 1,
                    },
                  ]);
                }
              }
            }}
            disabled={isAddingNewGrant}
            formVariant={errors.owner ? 'error' : undefined}
            helperText={errors.owner?.message}
            isPortal
          />
        )}
      />

      <Controller
        name="grantType"
        control={control}
        render={({ field: { value, onChange, ref } }) => (
          <Select
            innerRef={ref}
            label="Asset Type"
            value={options.GrantTypeOptions.find(option => option.value === value) || null}
            options={options.GrantTypeOptions}
            onChange={option => {
              onChange(option?.value);
              if (option) {
                const [dispositionOptions, exerciseOptions] = getDispositionExerciseOptions(
                  option.value,
                  options.OptionsPerGrantType,
                );
                setValue(
                  'vestingList',
                  vestingList?.map(vest => ({
                    ...vest,
                    DispositionStrategy: dispositionOptions ? vest.DispositionStrategy : null,
                    DispositionDate: dispositionOptions ? vest.DispositionDate : null,
                    DispositionYear: dispositionOptions ? vest.DispositionYear : null,
                    ExerciseStrategy: exerciseOptions ? vest.ExerciseStrategy : null,
                    ExerciseDate: exerciseOptions ? vest.ExerciseDate : null,
                    ExerciseYear: exerciseOptions ? vest.ExerciseYear : null,
                    ExpirationDate: option.value !== GranTypeDTO.Rsu ? vest.ExpirationDate : null,
                    PricePerShare: option.value !== GranTypeDTO.Rsu ? vest.PricePerShare : null,
                  })),
                );
              }
            }}
            formVariant={errors.grantType ? 'error' : undefined}
            helperText={errors.grantType?.message}
            isPortal
          />
        )}
      />

      <Controller
        control={control}
        name="totalShares"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            label={grantType === GranTypeDTO.Rsu ? 'Total Shares Granted' : 'Total Shares'}
            decimalScale={0}
            allowNegative={false}
            onValueChange={({ floatValue }) => {
              onChange(floatValue ?? null);
              // Auto-calculate market value when totalShares changes and product is selected
              if (product && typeof product.price === 'number' && typeof floatValue === 'number') {
                setValue('marketValue', product.price * floatValue);
              }
            }}
            formVariant={errors.totalShares ? 'error' : undefined}
            helperText={errors.totalShares?.message}
            {...field}
          />
        )}
      />

      <Controller
        control={control}
        name="marketValue"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            label="Market Value"
            leftIcon="far fa-dollar-sign"
            decimalScale={2}
            allowNegative={false}
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            disabled
            tooltip={
              'Market value is auto-calculated for listed securities (Total Shares * Price per Share)'
            }
            {...field}
          />
        )}
      />

      {options.AssetBucketingByManagementStyle && (
        <Controller
          name="orionManagementStyleId"
          control={control}
          render={({ field: { value, onChange, ref } }) => (
            <Select
              innerRef={ref}
              label="Management Style"
              value={managementStyleOptions.find(option => option.value === value) ?? null}
              options={managementStyleOptions}
              onChange={option => {
                const assetBucketIndex = managementStyleOptions.find(
                  managementStyleOption => managementStyleOption.value === option?.value,
                );

                if (assetBucketIndex) {
                  setValue(
                    'customAssetBucketIndex',
                    assetBucketIndex.defaultBucket !== null
                      ? assetBucketIndex.defaultBucket
                      : EXCLUDE_OPTION_VALUE,
                  );
                }

                onChange(option?.value);
              }}
              isPortal
            />
          )}
        />
      )}

      {options.DisplayAssetBucketing && (
        <Controller
          name="customAssetBucketIndex"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <Select
                label="Asset Bucketing"
                value={assetBucketOptions.find(option => option.value === value) || null}
                options={assetBucketOptions}
                onChange={option => onChange(option?.value)}
                disabled={
                  !options.AllowAssetBucketingCustomization ||
                  (options.AssetBucketingByManagementStyle && managementStyleId === null)
                }
                isPortal
              />
            );
          }}
        />
      )}

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Box>
            <Label css="display: block;" mb="4">
              Grant Date
            </Label>
            <Controller
              control={control}
              name="grantDate"
              render={({ field: { value, onChange, ref } }) => {
                const dateValue = value ? new Date(value) : undefined;
                return (
                  <SingleDatePicker
                    inputRef={ref}
                    date={dateValue ?? undefined}
                    onChange={onChange}
                    maxDate={new Date(options.LatestGrantDate)}
                    minDate={new Date(options.EarliestGrantDate)}
                    isWrapperWidth
                  />
                );
              }}
            />
          </Box>

          <Controller
            control={control}
            name="expectedReturn"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Projected Return"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip="Enter the average annual rate of return expected on this asset. If left blank, we will use the return for the selected asset class below."
                {...field}
              />
            )}
          />

          <Controller
            name="assetClass"
            control={control}
            render={({ field: { value, onChange, ref } }) => (
              <Select
                isClearable
                innerRef={ref}
                label="Asset Class"
                value={options.AssetClassOptions.find(option => option.value === value) || null}
                options={options.AssetClassOptions}
                onChange={option => onChange(option?.value)}
                isPortal
              />
            )}
          />

          <Controller
            control={control}
            name="earmarks"
            render={({ field: { value, onChange } }) => {
              return (
                <Select
                  isMulti
                  label="Account Earmark"
                  value={value}
                  options={options.EarmarkOptions}
                  onChange={earmarks => onChange(earmarks)}
                  isPortal
                />
              );
            }}
          />
        </>
      )}
    </Box>
  );
};
